import { type FC, type ReactNode, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import { viewMore, viewLess } from '../../finding/sbMessages.tsx';
import { PageLayout } from 'dibs-elements/exports/PageLayout.tsx';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Swiper } from 'dibs-pinch-zoom-swipe';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { QuickViewProvider } from 'dibs-search-product-tile/exports/QuickViewProvider.ts';
import { RESULTS_FIRST_ROW_COUNT } from '../../constants/sbConstants.ts';
import { useViewMore } from '../../finding/hooks/viewMore/useViewMore.ts';
import { locations } from '../../utils/tracking/shared/favoritesTracking.tsx';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider.jsx';
import { FullPageWidth } from '../../components/global/FullPageWidth/FullPageWidth.tsx';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider.tsx';
import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps.ts';

import classnames from 'classnames';

import styles from './SharedRelatedItemsWrapper.scss';

import { type SharedRelatedItemsWrapper_user$key } from './__generated__/SharedRelatedItemsWrapper_user.graphql.ts';
import { type SharedRelatedItemsWrapper_item$key } from './__generated__/SharedRelatedItemsWrapper_item.graphql.ts';

const userFragment = graphql`
    fragment SharedRelatedItemsWrapper_user on User {
        serviceId
    }
`;

const itemsFragment = graphql`
    fragment SharedRelatedItemsWrapper_item on Item @relay(plural: true) {
        ...FavoritesProvider_item
    }
`;

export type ChildrenProps = {
    currency: string;
    favorites: FavoritesProviderChildrenProps;
};

type Props = {
    user: SharedRelatedItemsWrapper_user$key;
    children: (props: ChildrenProps) => ReactNode;
    header: ReactNode;
    items: SharedRelatedItemsWrapper_item$key;
    isMobile: boolean;
    isClient: boolean;
    isDark?: boolean;
    showSwiper?: boolean;
    initialCount?: number;
    onItemsImpression: ({
        startIndex,
        itemsLengthToShow,
    }: {
        startIndex: number;
        itemsLengthToShow: number;
    }) => void;
    numberOfItemsToShow?: number;
};

export const SharedRelatedItemsWrapper: FC<Props> = ({
    user: userRef,
    children,
    header,
    items: itemsRef,
    isMobile,
    isClient,
    isDark,
    showSwiper = false,
    initialCount = RESULTS_FIRST_ROW_COUNT * 2,
    onItemsImpression = () => {},
}) => {
    const user = useFragment(userFragment, userRef);
    const items = useFragment(itemsFragment, itemsRef);

    const totalCount = items?.length;

    const [isViewMoreImpressionFired, setIsViewMoreImpressionFired] = useState(false);
    const {
        viewMoreEl,
        dispatch: toggleViewMore,
        visibleCount,
    } = useViewMore<HTMLButtonElement>({
        totalCount,
        revealCount: totalCount - initialCount,
        initialCount,
    });

    const trackViewMoreItemsImpression = (): void => {
        if (!isViewMoreImpressionFired) {
            onItemsImpression({
                startIndex: initialCount,
                itemsLengthToShow: totalCount - initialCount,
            });
            setIsViewMoreImpressionFired(true);
        }
    };

    return (
        <>
            <VisibilityTracker
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        onItemsImpression({
                            startIndex: 0,
                            itemsLengthToShow: visibleCount,
                        });
                    }
                }}
            />
            <FavoritesProvider
                disable={!isClient}
                items={items}
                location={locations.SEARCH_BROWSE}
                userId={user?.serviceId}
                fetchFolder={!isMobile}
            >
                {({ props: favorites }: { props: FavoritesProviderChildrenProps }) => (
                    <CurrencyProvider
                        render={({ currency }: { currency: ChildrenProps['currency'] }) => (
                            <QuickViewProvider>
                                <PageLayout setViewportBackground={isDark}>
                                    <div
                                        className={classnames(styles.container, {
                                            [styles.topSpacer]: !isDark && !showSwiper,
                                        })}
                                    >
                                        <HeadingLevel>
                                            {Heading => (
                                                <Heading className={styles.header}>
                                                    {header}
                                                </Heading>
                                            )}
                                        </HeadingLevel>
                                        {showSwiper ? (
                                            <FullPageWidth>
                                                <Swiper itemsPerPage={2.5} itemsTopAlign>
                                                    {children({
                                                        currency,
                                                        favorites,
                                                    })}
                                                </Swiper>
                                            </FullPageWidth>
                                        ) : (
                                            <div
                                                className={classnames(styles.gridContainer, {
                                                    [styles.limit4Items]:
                                                        visibleCount < totalCount &&
                                                        initialCount === RESULTS_FIRST_ROW_COUNT,
                                                    [styles.limit8Items]:
                                                        visibleCount < totalCount &&
                                                        initialCount ===
                                                            RESULTS_FIRST_ROW_COUNT * 2,
                                                })}
                                            >
                                                {children({
                                                    currency,
                                                    favorites,
                                                })}
                                            </div>
                                        )}
                                        {!showSwiper && totalCount > initialCount && (
                                            <div className={styles.viewMoreSection}>
                                                <Button
                                                    className={styles.viewMoreButton}
                                                    buttonRef={viewMoreEl}
                                                    size="medium"
                                                    type="secondary"
                                                    onClick={() => {
                                                        trackViewMoreItemsImpression();
                                                        toggleViewMore();
                                                    }}
                                                >
                                                    {visibleCount < totalCount
                                                        ? viewMore
                                                        : viewLess}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </PageLayout>
                            </QuickViewProvider>
                        )}
                    />
                )}
            </FavoritesProvider>
        </>
    );
};
